/**
 * CloudWatch-Timestamp-Behandlung (Spec 143 §5.3, Trap §8.2)
 *
 * Die Facade liefert CW-Event-Timestamps im Format "YYYY-MM-DD HH:mm:ss.SSS" —
 * UTC, aber OHNE Zeitzonen-Suffix und kein ISO 8601. `new Date(ts)` wuerde
 * den Wert als LOKALZEIT interpretieren (stiller Zeitverschiebungs-Bug).
 *
 * Anzeige-Regel (verbindlich): CW-Timestamps werden in UTC mit sichtbarem
 * "UTC"-Label dargestellt — bevorzugt der rohe String selbst. UTC-Parsing
 * mit anschliessender Lokalzeit-Formatierung wuerde den Shift nur verlagern.
 * UTC-Anzeige haelt die Werte vergleichbar mit rawLog-Inhalten (UTC-ISO)
 * und externen CloudWatch-Abfragen.
 */

const CW_TS_PATTERN = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})\.(\d{3})$/;

/**
 * Parst einen CW-Timestamp explizit als UTC.
 * Nur fuer Sortierung/Gruppierung verwenden — NICHT fuer die Anzeige
 * (Anzeige laeuft ueber formatCwTimestampUtc).
 * ISO-Strings mit Zeitzone (z.B. "…Z") werden unveraendert geparst.
 */
export function parseCwTimestamp(ts: string): Date {
  const m = CW_TS_PATTERN.exec(ts);
  if (m) {
    return new Date(
      Date.UTC(
        Number(m[1]),
        Number(m[2]) - 1,
        Number(m[3]),
        Number(m[4]),
        Number(m[5]),
        Number(m[6]),
        Number(m[7]),
      ),
    );
  }
  return new Date(ts);
}

/**
 * Anzeige-String fuer CW-Timestamps: UTC mit sichtbarem Label.
 * CW-Format wird roh durchgereicht (es IST bereits UTC, millisekunden-genau);
 * ISO-Eingaben werden ins gleiche Format normalisiert. Unparsebare Eingaben
 * kommen unveraendert zurueck (defensiv, Sparse-Daten).
 */
export function formatCwTimestampUtc(ts: string): string {
  if (CW_TS_PATTERN.test(ts)) {
    return `${ts} UTC`;
  }
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) {
    return ts;
  }
  const pad = (n: number, len = 2): string => String(n).padStart(len, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.` +
    `${pad(d.getUTCMilliseconds(), 3)} UTC`
  );
}
